import type { Page, Pagination } from '@aigc/types';
import { md5 } from '@noble/hashes/legacy.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { isHTTPError } from 'ky';
import { http } from '../common/http-client';
import type {
  FileDto,
  OssSignatureDto,
  RegisterFileDto,
  UploadFileOutputDto,
} from './model';

async function calculateFileMD5(file: File): Promise<string> {
  const hasher = md5.create();
  const chunkSize = 4 * 1024 * 1024;

  for (let offset = 0; offset < file.size; offset += chunkSize) {
    const buffer = await file.slice(offset, offset + chunkSize).arrayBuffer();

    hasher.update(new Uint8Array(buffer));
  }

  return bytesToHex(hasher.digest());
}

export const uploadFile = async (
  file: File,
  options?: {
    onProgress: (event: ProgressEvent<XMLHttpRequestEventTarget>) => void;
  },
): Promise<UploadFileOutputDto> => {
  const hash = await calculateFileMD5(file);

  try {
    return await http
      .patch<UploadFileOutputDto>(`files/hash:${hash}`, {
        json: {
          filename: file.name,
          mime: file.type,
        },
      })
      .json();
  } catch (error) {
    if (isHTTPError(error) && error.response.status !== 404) {
      throw error;
    }

    const signature = await http
      .get('oss/signature', {
        searchParams: {
          filename: file.name,
        },
      })
      .json<OssSignatureDto>();

    const form = new FormData();
    form.set('name', file.name);
    form.set('policy', signature.policy);
    form.set('OSSAccessKeyId', signature.oss_access_key_id);
    form.set('signature', signature.signature);
    form.set('key', signature.key);
    form.set('success_action_status', '200');
    form.set('x-oss-object-acl', 'public-read');
    form.set('file', file);

    // fech 对上传进度支持不好，使用 XMLHttpRequest 来实现上传功能，以便支持上传进度的回调
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', signature.host, true);
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(xhr.responseText));
        }
      });
      xhr.addEventListener('error', () =>
        reject(new Error('网络错误，上传失败')),
      );
      xhr.upload.addEventListener('progress', (event) => {
        options?.onProgress(event);
      });
      xhr.addEventListener('abort', () => {
        reject(new Error('文件已取消'));
      });
      xhr.addEventListener('timeout', () => {
        reject(new Error('上传超时，请稍后重试'));
      });
      xhr.send(form);
    });

    const registerFileDto: RegisterFileDto = {
      filename: file.name,
      mime: file.type,
      oss_host: signature.host,
      oss_object_key: signature.key,
      source: 'user',
      content_md5: hash,
    };

    return await http
      .post<UploadFileOutputDto>('/files', {
        json: registerFileDto,
      })
      .json();
  }
};

export const queryUploadedFiles = async (pagination?: Pagination) => {
  return http
    .get('files', {
      searchParams: {
        page: pagination?.page,
        size: pagination?.size,
      },
    })
    .json<Page<FileDto>>();
};
